import {
  CreateButton,
  FunctionField,
  TextField,
  TopToolbar,
  ListContextProvider,
  useListController,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { Chip, Stack, Typography } from "@mui/material";
import { UsersListAside } from "./UsersListAside";
import { ResourceName, User } from "@/types";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { StyledDatagrid } from "@/components/ui/react-admin/StyledDatagrid";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { PageHeader } from "@/components/common/PageHeader";
import PeopleIcon from "@mui/icons-material/People";

const RolesColumn = ({ record }: { record?: User }) => {
  const roles = record?.assigned_role_names ?? [];
  if (roles.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {roles.map((name) => (
        <Chip key={name} label={name} size="small" variant="outlined" />
      ))}
    </Stack>
  );
};

const UserListActions = ({ loading }: { loading?: boolean }) => (
  <TopToolbar>
    <CreateButton resource={ResourceName.USERS} disabled={loading} />
    <ChunkedExportButton disabled={loading} />
  </TopToolbar>
);

export const UsersList = () => {
  const listContext = useListController({ resource: ResourceName.USERS });
  const { isPending: loading } = listContext;

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Users"
        rightContent={<UserListActions loading={loading} />}
        headerIcon={<PeopleIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List perPage={50} aside={<UsersListAside />} actions={false} loading={loading}>
        <StyledDatagrid>
          <TextField source="full_name" label="Name" />
          <FunctionField
            label="Role"
            source="assigned_role_names"
            sortable={false}
            render={(record: User) => <RolesColumn record={record} />}
          />
          <TextField source="email" label="Email" />
          <TextField source="manager.full_name" label="Manager" />
          <StatusChipField source="status" />
        </StyledDatagrid>
      </List>
    </ListContextProvider>
  );
};
